import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import axios from 'axios';
import { buildApiUrl, toUploadUrl } from '../../config/api';

const AdminDestinations = () => {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedDest, setSelectedDest] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        region: 'srinagar',
        title: '',
        description: '',
        long_description: '',
        best_time: '',
        ideal_duration: '',
        altitude: '',
        rating: 0,
        reviews: 0,
        status: 'active',
        highlights: [],
        activities: []
    });
    const [highlightInput, setHighlightInput] = useState('');
    const [activityInput, setActivityInput] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);

    const getToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('❌ No token found');
            alert('Session expired. Please login again.');
            window.location.href = '/admin/login';
            return null;
        }
        return token;
    };

    const fetchDestinations = useCallback(async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) return;
            
            const response = await axios.get(buildApiUrl('/destinations'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token,
                    'Content-Type': 'application/json'
                }
            });
            
            let destinationsData = [];
            if (response.data.success && response.data.data) {
                destinationsData = response.data.data;
            } else if (Array.isArray(response.data)) {
                destinationsData = response.data;
            } else if (response.data.destinations) {
                destinationsData = response.data.destinations;
            }
            
            setDestinations(destinationsData);
            console.log('✅ Destinations fetched:', destinationsData.length);
        } catch (error) {
            console.error('Error fetching destinations:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert('Failed to fetch destinations');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            alert('Please login to access admin panel');
            window.location.href = '/admin/login';
            return;
        }
        fetchDestinations();
    }, [fetchDestinations]);

    // ✅ FIXED: Proper image URL handler
    const getImageUrl = (imagePath) => {
        if (!imagePath) {
            return 'https://placehold.co/50x50/1a1a1a/ffffff?text=No+Img';
        }
        
        // If already full URL
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        
        // Remove leading slashes
        let cleanPath = imagePath.replace(/^\/+/, '');
        
        return toUploadUrl(cleanPath);
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const addHighlight = () => {
        if (highlightInput.trim()) {
            setFormData({
                ...formData,
                highlights: [...formData.highlights, highlightInput.trim()]
            });
            setHighlightInput('');
        }
    };

    const removeHighlight = (index) => {
        const newHighlights = formData.highlights.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            highlights: newHighlights
        });
    };

    const addActivity = () => {
        if (activityInput.trim()) {
            setFormData({
                ...formData,
                activities: [...formData.activities, activityInput.trim()]
            });
            setActivityInput('');
        }
    };

    const removeActivity = (index) => {
        const newActivities = formData.activities.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            activities: newActivities
        });
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        
        const validFiles = files.filter(file => {
            if (!file.type.startsWith('image/')) {
                alert(`${file.name} is not an image file`);
                return false;
            }
            if (file.size > 10 * 1024 * 1024) {
                alert(`${file.name} is too large. Max size 10MB`);
                return false;
            }
            return true;
        });

        setSelectedFiles(validFiles);
        const urls = validFiles.map(file => URL.createObjectURL(file));
        setPreviewUrls(urls);
        
        console.log(`✅ Selected ${validFiles.length} valid files`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.region || !formData.description) {
            alert('Please fill in all required fields');
            return;
        }

        setUploading(true);
        const token = getToken();
        if (!token) {
            setUploading(false);
            return;
        }
        
        const formDataToSend = new FormData();
        
        Object.keys(formData).forEach(key => {
            if (key === 'highlights' || key === 'activities') {
                if (formData[key].length > 0) {
                    formDataToSend.append(key, JSON.stringify(formData[key]));
                }
            } else if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
                formDataToSend.append(key, formData[key]);
            }
        });

        if (selectedFiles.length > 0) {
            selectedFiles.forEach((file, index) => {
                formDataToSend.append('images', file);
            });
        }

        try {
            const url = selectedDest
                ? buildApiUrl(`/destinations/${selectedDest.id}/update`)
                : buildApiUrl('/destinations');
            
            const method = 'post';

            console.log(`🚀 Sending ${method.toUpperCase()} request to:`, url);
            
            const response = await axios({
                method,
                url,
                data: formDataToSend,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token
                }
            });

            console.log('✅ Response:', response.data);
            alert(`Destination ${selectedDest ? 'updated' : 'created'} successfully!`);
            setShowModal(false);
            resetForm();
            fetchDestinations();
        } catch (error) {
            console.error('❌ Error saving destination:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert(error.response?.data?.message || 'Failed to save destination');
            }
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this destination?')) return;

        const token = getToken();
        if (!token) return;

        try {
            await axios.delete(buildApiUrl(`/destinations/${id}`), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token
                }
            });
            alert('Destination deleted successfully!');
            fetchDestinations();
        } catch (error) {
            console.error('Error deleting destination:', error);
            alert('Failed to delete destination');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            region: 'srinagar',
            title: '',
            description: '',
            long_description: '',
            best_time: '',
            ideal_duration: '',
            altitude: '',
            rating: 0,
            reviews: 0,
            status: 'active',
            highlights: [],
            activities: []
        });
        setSelectedFiles([]);
        setPreviewUrls([]);
        setSelectedDest(null);
        setHighlightInput('');
        setActivityInput('');
    };

    const openEditModal = (dest) => {
        setSelectedDest(dest);
        setFormData({
            name: dest.name || '',
            region: dest.region || 'srinagar',
            title: dest.title || '',
            description: dest.description || '',
            long_description: dest.long_description || '',
            best_time: dest.best_time || '',
            ideal_duration: dest.ideal_duration || '',
            altitude: dest.altitude || '',
            rating: dest.rating || 0,
            reviews: dest.reviews || 0,
            status: dest.status || 'active',
            highlights: dest.highlights || [],
            activities: dest.activities || []
        });
        setShowModal(true);
    };

    const regions = [
        { value: 'srinagar', label: 'Srinagar' },
        { value: 'gulmarg', label: 'Gulmarg' },
        { value: 'pahalgam', label: 'Pahalgam' },
        { value: 'sonmarg', label: 'Sonmarg' },
        { value: 'patnitop', label: 'Patnitop' },
        { value: 'jammu', label: 'Jammu' },
        { value: 'kargil', label: 'Kargil' }
    ];

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader 
                    title="Manage Destinations" 
                    subtitle="Add, edit, or remove destinations"
                />

                <div className="admin-content">
                    <div className="content-header">
                        <h2>All Destinations</h2>
                        <button className="add-btn" onClick={() => {
                            resetForm();
                            setShowModal(true);
                        }}>
                            <i className="fas fa-plus"></i> Add Destination
                        </button>
                    </div>

                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading destinations...</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Image</th>
                                        <th>Name</th>
                                        <th>Region</th>
                                        <th>Rating</th>
                                        <th>Reviews</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {destinations.length > 0 ? (
                                            destinations.map(dest => (
                                                <tr key={dest.id}>
                                                    <td className="image-cell">
                                                        <img 
                                                            src={getImageUrl(dest.image)} 
                                                            alt={dest.name}
                                                            className="table-thumb"
                                                            onError={(e) => {
                                                                console.log('Image load error:', dest.image);
                                                                e.target.src = 'https://placehold.co/50x50/1a1a1a/ffffff?text=No+Img';
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <strong>{dest.name}</strong>
                                                        {dest.title && <div className="title-text">{dest.title}</div>}
                                                    </td>
                                                    <td>
                                                        <span className="region-badge">{dest.region}</span>
                                                    </td>
                                                    <td>
                                                        <span className="rating-badge">
                                                            <i className="fas fa-star"></i> {dest.rating || 0}
                                                        </span>
                                                    </td>
                                                    <td>{dest.reviews || 0}</td>
                                                    <td>
                                                        <span className={`status-badge ${dest.status}`}>
                                                            {dest.status}
                                                        </span>
                                                    </td>
                                                    <td className="actions-cell">
                                                        <button 
                                                            className="action-btn edit"
                                                            onClick={() => openEditModal(dest)}
                                                            title="Edit"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button 
                                                            className="action-btn delete"
                                                            onClick={() => handleDelete(dest.id)}
                                                            title="Delete"
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                        <Link 
                                                            to={`/destinations/${dest.id}`} 
                                                            className="action-btn view"
                                                            target="_blank"
                                                            title="View"
                                                        >
                                                            <i className="fas fa-eye"></i>
                                                        </Link>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="empty-state">
                                                    <i className="fas fa-map-marker-alt"></i>
                                                    <p>No destinations found</p>
                                                    <button className="btn-primary" onClick={() => {
                                                        resetForm();
                                                        setShowModal(true);
                                                    }}>
                                                        Add Your First Destination
                                                    </button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Add/Edit Modal */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-container modal-large" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{selectedDest ? 'Edit Destination' : 'Add New Destination'}</h2>
                                <button className="close-btn" onClick={() => setShowModal(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="modal-form">
                                <div className="form-scroll">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Destination Name *</label>
                                            <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g., Srinagar" required />
                                        </div>
                                        <div className="form-group">
                                            <label>Region *</label>
                                            <select name="region" value={formData.region} onChange={handleInputChange} required>
                                                {regions.map(region => (
                                                    <option key={region.value} value={region.value}>{region.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Title</label>
                                        <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="e.g., The Heart of Kashmir" />
                                    </div>

                                    <div className="form-group">
                                        <label>Short Description *</label>
                                        <textarea name="description" value={formData.description} onChange={handleInputChange} rows="3" placeholder="Brief description..." required></textarea>
                                    </div>

                                    <div className="form-group">
                                        <label>Long Description</label>
                                        <textarea name="long_description" value={formData.long_description} onChange={handleInputChange} rows="5" placeholder="Detailed description..."></textarea>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Best Time to Visit</label>
                                            <input type="text" name="best_time" value={formData.best_time} onChange={handleInputChange} placeholder="e.g., April to October" />
                                        </div>
                                        <div className="form-group">
                                            <label>Ideal Duration</label>
                                            <input type="text" name="ideal_duration" value={formData.ideal_duration} onChange={handleInputChange} placeholder="e.g., 2-3 days" />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Altitude</label>
                                            <input type="text" name="altitude" value={formData.altitude} onChange={handleInputChange} placeholder="e.g., 1,585 m" />
                                        </div>
                                        <div className="form-group">
                                            <label>Rating (0-5)</label>
                                            <input type="number" step="0.1" min="0" max="5" name="rating" value={formData.rating} onChange={handleInputChange} />
                                        </div>
                                        <div className="form-group">
                                            <label>Reviews Count</label>
                                            <input type="number" min="0" name="reviews" value={formData.reviews} onChange={handleInputChange} />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Highlights</label>
                                        <div className="input-with-button">
                                            <input type="text" value={highlightInput} onChange={(e) => setHighlightInput(e.target.value)} placeholder="Add a highlight" />
                                            <button type="button" onClick={addHighlight} className="add-item-btn">Add</button>
                                        </div>
                                        <div className="tags-container">
                                            {formData.highlights.map((item, index) => (
                                                <span key={index} className="tag">{item}<i className="fas fa-times" onClick={() => removeHighlight(index)}></i></span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Activities</label>
                                        <div className="input-with-button">
                                            <input type="text" value={activityInput} onChange={(e) => setActivityInput(e.target.value)} placeholder="Add an activity" />
                                            <button type="button" onClick={addActivity} className="add-item-btn">Add</button>
                                        </div>
                                        <div className="tags-container">
                                            {formData.activities.map((item, index) => (
                                                <span key={index} className="tag">{item}<i className="fas fa-times" onClick={() => removeActivity(index)}></i></span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Images</label>
                                        <input type="file" multiple accept="image/*" onChange={handleFileChange} />
                                        <small className="help-text">Supported formats: JPEG, PNG, GIF, WEBP (Max 10MB each)</small>
                                        {previewUrls.length > 0 && (
                                            <div className="image-previews">
                                                {previewUrls.map((url, index) => (
                                                    <div key={index} className="preview-item">
                                                        <img src={url} alt={`Preview ${index + 1}`} />
                                                        <span>{index + 1}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {selectedDest && selectedDest.image && !selectedFiles.length && (
                                            <div className="existing-image">
                                                <p>Current image:</p>
                                                <img src={getImageUrl(selectedDest.image)} alt="Current" className="existing-img" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label>Status</label>
                                        <select name="status" value={formData.status} onChange={handleInputChange}>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="submit-btn" disabled={uploading}>
                                        {uploading ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : (selectedDest ? 'Update Destination' : 'Create Destination')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
                <style>{`/* AdminDestinations.css - Add these styles */

.image-cell {
    width: 60px;
}

.table-thumb {
    width: 50px;
    height: 50px;
    border-radius: 10px;
    object-fit: cover;
    background: #f1f5f9;
}

.title-text {
    font-size: 0.7rem;
    color: #64748b;
    margin-top: 0.2rem;
}

.region-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    background: #f1f5f9;
    border-radius: 20px;
    font-size: 0.7rem;
    text-transform: capitalize;
}

.rating-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.2rem 0.6rem;
    background: #fef9c3;
    border-radius: 20px;
    font-size: 0.7rem;
    color: #854d0e;
}

.rating-badge i {
    font-size: 0.6rem;
}

.status-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: capitalize;
}

.status-badge.active {
    background: #dcfce7;
    color: #166534;
}

.status-badge.inactive {
    background: #fee2e2;
    color: #991b1b;
}

.actions-cell {
    white-space: nowrap;
}

.action-btn {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: #f1f5f9;
    color: #64748b;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 0.25rem;
    transition: all 0.2s;
    text-decoration: none;
}

.action-btn:hover {
    transform: translateY(-1px);
}

.action-btn.edit:hover {
    background: #e3f2fd;
    color: #1976d2;
}

.action-btn.delete:hover {
    background: #fee2e2;
    color: #d32f2f;
}

.action-btn.view:hover {
    background: #e8f5e9;
    color: #2e7d32;
}

.existing-image {
    margin-top: 0.5rem;
}

.existing-image p {
    font-size: 0.7rem;
    color: #64748b;
    margin-bottom: 0.25rem;
}

.existing-img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #2e7d32;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.empty-state {
    text-align: center;
    padding: 3rem !important;
    color: #94a3b8;
}

.empty-state i {
    font-size: 3rem;
    margin-bottom: 1rem;
}

.btn-primary {
    background: #2e7d32;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    cursor: pointer;
    margin-top: 1rem;
}
.close-btn:hover {
    background: #f1f5f9;
    color: #1e293b;
}

.modal-form {
    padding: 1.5rem;
}

.form-scroll {
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 0.5rem;
}

.form-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 1rem;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.85rem;
    font-weight: 500;
    color: #475569;
}

.form-group input,
.form-group select,
.form-group textarea {
    width: 100%;
    padding: 0.6rem 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.85rem;
    transition: all 0.2s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
    outline: none;
    border-color: #2e7d32;
    box-shadow: 0 0 0 3px rgba(46,125,50,0.1);
}

/* Input with Button */
.input-with-button {
    display: flex;
    gap: 0.5rem;
}

.input-with-button input {
    flex: 1;
}

.add-item-btn {
    padding: 0.6rem 1rem;
    background: #2e7d32;
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    font-size: 0.8rem;
}

.add-item-btn:hover {
    background: #1b5e20;
}

/* Tags */
.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.tag {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.7rem;
    background: #f1f5f9;
    border-radius: 20px;
    font-size: 0.75rem;
    color: #1e293b;
}

.tag i {
    cursor: pointer;
    color: #94a3b8;
    font-size: 0.7rem;
}

.tag i:hover {
    color: #d32f2f;
}

/* Image Previews */
.image-previews {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.preview-item {
    position: relative;
    width: 70px;
    height: 70px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
}

.preview-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.preview-item span {
    position: absolute;
    bottom: 0;
    right: 0;
    background: rgba(0,0,0,0.6);
    color: white;
    font-size: 0.6rem;
    padding: 0.1rem 0.3rem;
    border-radius: 4px 0 0 0;
}

/* Help Text */
.help-text {
    display: block;
    font-size: 0.7rem;
    color: #94a3b8;
    margin-top: 0.25rem;
}

/* Modal Footer */
.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
}

.cancel-btn {
    padding: 0.6rem 1.2rem;
    background: #f1f5f9;
    color: #475569;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 500;
}

.cancel-btn:hover {
    background: #e2e8f0;
}

.submit-btn {
    padding: 0.6rem 1.2rem;
    background: #2e7d32;
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.submit-btn:hover:not(:disabled) {
    background: #1b5e20;
    transform: translateY(-1px);
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Responsive */
@media (max-width: 1024px) {
    .admin-main {
        margin-left: 0;
        padding: 1rem;
    }
}

@media (max-width: 768px) {
    .form-row {
        grid-template-columns: 1fr;
    }
    
    .modal-container {
        width: 95%;
        margin: 1rem;
    }
    
    .actions-cell {
        display: flex;
        gap: 0.25rem;
    }
}


/* Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-container {
    background: white;
    border-radius: 24px;
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease;
}

.modal-large {
    max-width: 1000px;
}

@keyframes slideUp {
    from { transform: translateY(50px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    background: white;
    border-radius: 24px 24px 0 0;
}

.modal-header h2 {
    font-size: 1.2rem;
    color: #1e293b;
    margin: 0;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #94a3b8;
    width: 32px;
    height: 32px;
    border-radius: 8px;
}

.close-btn:hover {
    background: #f1f5f9;
}

/* Modal Tabs */
.modal-tabs {
    display: flex;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    flex-wrap: wrap;
}

.tab-btn {
    padding: 0.5rem 1rem;
    background: none;
    border: none;
    border-radius: 30px;
    font-size: 0.8rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #64748b;
    transition: all 0.2s;
}

.tab-btn i {
    font-size: 0.8rem;
}

.tab-btn:hover {
    background: #e2e8f0;
}

.tab-btn.active {
    background: #2e7d32;
    color: white;
}

/* Modal Form */
.modal-form {
    padding: 1.5rem;
}

.form-scroll {
    max-height: 55vh;
    overflow-y: auto;
    padding-right: 0.5rem;
}

.form-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 1rem;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.8rem;
    font-weight: 500;
    color: #475569;
}

.form-group input,
.form-group select,
.form-group textarea {
    width: 100%;
    padding: 0.6rem 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.85rem;
}

.form-group.checkbox {
    display: flex;
    align-items: center;
}

.form-group.checkbox label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    cursor: pointer;
}

/* Input with Button */
.input-with-button {
    display: flex;
    gap: 0.5rem;
}

.input-with-button input {
    flex: 1;
}

.add-item-btn {
    padding: 0.6rem 1rem;
    background: #2e7d32;
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
}

/* Tags */
.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.tag {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.7rem;
    background: #f1f5f9;
    border-radius: 20px;
    font-size: 0.75rem;
}

.tag i {
    cursor: pointer;
    color: #94a3b8;
}

.tag i:hover {
    color: #d32f2f;
}

.empty-message {
    color: #94a3b8;
    font-size: 0.75rem;
    margin-top: 0.5rem;
}

/* Itinerary & Stay Items */
.itinerary-item, .stay-item {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1rem;
    margin-bottom: 1rem;
}

.itinerary-header, .stay-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.day-badge {
    background: #2e7d32;
    color: white;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    font-size: 0.7rem;
}

.stay-type-badge {
    background: #9f7aea;
    color: white;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    font-size: 0.7rem;
}

.remove-btn {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
}

.remove-btn:hover {
    color: #d32f2f;
}

.itinerary-meta {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
    font-size: 0.7rem;
    color: #64748b;
}

.stay-amenities {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.amenity-tag {
    padding: 0.2rem 0.5rem;
    background: white;
    border-radius: 20px;
    font-size: 0.7rem;
    color: #475569;
}

.stay-description {
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 0.5rem;
}

/* Image Previews */
.image-previews {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.preview-img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.add-btn {
    background: #2e7d32;
    color: white;
    border: none;
    padding: 0.6rem 1rem;
    border-radius: 10px;
    cursor: pointer;
    margin-top: 0.5rem;
}

/* Modal Footer */
.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
}

.cancel-btn {
    padding: 0.6rem 1.2rem;
    background: #f1f5f9;
    color: #475569;
    border: none;
    border-radius: 10px;
    cursor: pointer;
}

.submit-btn {
    padding: 0.6rem 1.2rem;
    background: #2e7d32;
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.submit-btn:hover:not(:disabled) {
    background: #1b5e20;
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Responsive */
@media (max-width: 1024px) {
    .admin-main {
        margin-left: 0;
        padding: 1rem;
    }
    
    .form-row {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .modal-tabs {
        overflow-x: auto;
        flex-wrap: nowrap;
    }
    
    .tab-btn {
        white-space: nowrap;
    }
    
    .modal-container {
        width: 95%;
        margin: 1rem;
    }
}

`}
                    </style>
            </div>
        );
    };
    
    export default AdminDestinations;
